import { FC, useEffect, useRef, useState } from "react";
import { FormContainerStateReturnType, useTrans } from "../../../hooks";
import {
    VStack,
    Input,
    useToast,
    HStack,
    Button,
    Center,
    Divider,
    Box,
    Text,
    Progress,
} from "@chakra-ui/react";
import {
    CompanyApi,
    CompanyDocument as Entity,
    CompanyDocumentApi as EntityApi,
    Upload,
    UploadApi,
} from "../../../apis";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { schema } from "./schema";
import { AppFormContainer } from "../../../containers";
import { AppFormControl, AppIcon } from "../../../components";
import { APPC } from "../../../config";
import { Canceler } from "axios";
import { UnprocessableErrorResponse } from "../../../services/ApiPlatform/models";

export const CompanyDocumentForm: FC<{
    formContainerState: FormContainerStateReturnType;
    companyId: string;
}> = ({ formContainerState, companyId }) => {
    // hook
    const { t } = useTrans();
    const toast = useToast();

    // state & const
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<Entity>(new Entity());

    const [, setLoadingAttatch] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [selectedUploadedFile, setSelectedUploadedFile] = useState<Upload>();
    const [backendError, setBackendError] = useState("");
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const cancelTokenSourcesRef = useRef<Canceler[]>([]);

    const fileTypeInfo =
        APPC.BE.Upload.FILETYPEINFO[
            `FILETYPEINFO_${APPC.BE.Upload.FILETYPE.FILETYPE_COMPANY_FILE}`
        ];

    const {
        handleSubmit,
        register,
        reset,
        setError,
        formState: { errors },
    } = useForm<Entity>({
        resolver: yupResolver(schema(t)),
        mode: "onSubmit",
    });

    // handler
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (
                file &&
                fileTypeInfo.maxSize &&
                file.size > fileTypeInfo.maxSize * 1024 * 1024
            ) {
                toast({
                    title: t("cmn:val.fileSize.notValid", {
                        size: fileTypeInfo.maxSize,
                    }),
                    status: "error",
                });
            } else {
                setSelectedFile(file);
                submitAttatchHandler(file);
            }
        }
    };

    const handleCancelAttatch = () => {
        cancelTokenSourcesRef.current.forEach((cancelFn) => {
            if (cancelFn) {
                cancelFn();
            }
        });
        cancelTokenSourcesRef.current = [];
        setSelectedFile(null);
        setSelectedUploadedFile(undefined);
        setUploadProgress(0);
        setLoadingAttatch(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const submitAttatchHandler = (file: File) => {
        setLoadingAttatch(true);
        const uploadFormData = new FormData();
        uploadFormData.set(
            "fileType",
            APPC.BE.Upload.FILETYPE.FILETYPE_COMPANY_FILE
        );
        uploadFormData.set("file", file);

        UploadApi.upload<Upload, FormData>(
            uploadFormData,
            (c) => {
                cancelTokenSourcesRef.current.push(c);
            },
            (p) => setUploadProgress(p)
        )
            .then(({ response, error, errorMessage }) => {
                if (error instanceof UnprocessableErrorResponse) {
                    const { violations } = error;
                    Object.keys(violations).map((key) => {
                        setBackendError(violations[key]);
                    });
                } else if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    setSelectedUploadedFile(response);
                }
            })
            .finally(() => {
                setUploadProgress(0);
                setLoadingAttatch(false);
            });
    };

    const onSubmitHandler = (formData: Entity) => {
        formData.company = CompanyApi.toResourceIRI(companyId);
        if (selectedUploadedFile) {
            formData.attachmentName = selectedUploadedFile.fileName;
        } else {
            if (!data.attachmentName) {
                setError("attachmentName", {
                    type: "validation",
                    message: t("cmn:val.required"),
                });
                return;
            }
        }
        setLoading(true);
        EntityApi.createOrUpdate<Entity, Entity>(
            formContainerState.entityId,
            formData
        )
            .then(({ response, errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    toast({
                        title: t("cmn:message.save.success"),
                        status: "success",
                    });
                    reset();
                    formContainerState.close();
                }
            })
            .finally(() => setLoading(false));
    };

    // useEffect
    useEffect(() => {
        if (!formContainerState.entityId) {
            reset();
            setData(new Entity());
            return;
        }
        setLoading(true);
        EntityApi.getItem<Entity>(formContainerState.entityId)
            .then(({ errorMessage, isNotFound, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (isNotFound) {
                    toast({
                        title: t("cmn:message.entityNotFound"),
                        status: "error",
                    });
                } else if (response !== null) {
                    reset();
                    setData(response);
                }
            })
            .finally(() => setLoading(false));
    }, [formContainerState.entityId]);

    return (
        <AppFormContainer
            title={t("padm.CompanyDocumentForm:text.pageTitle")}
            state={formContainerState}
            type={APPC.FORM_CONTAINER}
            size="xl"
        >
            <form
                id="app-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmitHandler)();
                }}
            >
                <VStack gap={5} alignItems={"stretch"}>
                    <AppFormControl
                        label={t("ent.CompanyDocument:name.label")}
                        isInvalid={!!errors.name}
                        message={errors.name?.message}
                        isLoading={loading}
                    >
                        <Input
                            type="text"
                            placeholder={t(
                                "ent.CompanyDocument:name.placeholder"
                            )}
                            {...register("name")}
                            defaultValue={data.name}
                        />
                    </AppFormControl>
                    <AppFormControl
                        label={t("ent.CompanyDocument:attachmentName.label")}
                        isInvalid={!!errors.attachmentName}
                        message={errors.attachmentName?.message}
                        isLoading={loading}
                    >
                        <HStack
                            h={"full"}
                            px={6}
                            py={2}
                            justifyContent={"space-between"}
                            bg="var(--chakra-colors-primaryT80)"
                            gap={4}
                        >
                            <HStack>
                                <input
                                    type="file"
                                    hidden
                                    ref={fileInputRef}
                                    onChange={handleFileSelect}
                                />
                                <Button
                                    size={"sm"}
                                    variant={"unstyled"}
                                    onClick={() => {
                                        fileInputRef.current?.click();
                                    }}
                                >
                                    <AppIcon
                                        name={"icl-paperclip"}
                                        w="1.25rem"
                                        color={"var(--chakra-colors-primary)"}
                                    />
                                </Button>
                                {(selectedFile || data.attachmentName) && (
                                    <>
                                        <Center height="1.75rem">
                                            <Divider
                                                orientation="vertical"
                                                borderColor={
                                                    "var(--chakra-colors-primary)"
                                                }
                                            />
                                        </Center>
                                        <HStack>
                                            <Box>
                                                <Text
                                                    textStyle={"regularSm"}
                                                    lineHeight={1.1}
                                                    noOfLines={1}
                                                    color={
                                                        backendError
                                                            ? "var(--chakra-colors-destructive)"
                                                            : ""
                                                    }
                                                >
                                                    {backendError
                                                        ? backendError
                                                        : selectedUploadedFile
                                                          ? selectedUploadedFile.fileName
                                                          : selectedFile?.name ||
                                                            data.attachmentName}
                                                </Text>
                                                {uploadProgress > 0 &&
                                                    uploadProgress <= 100 && (
                                                        <Progress
                                                            colorScheme="green"
                                                            height="3px"
                                                            value={
                                                                uploadProgress
                                                            }
                                                            mt={1.5}
                                                        />
                                                    )}
                                            </Box>
                                            {!data.attachmentName && (
                                                <Button
                                                    size={"sm"}
                                                    variant={"unstyled"}
                                                    onClick={
                                                        handleCancelAttatch
                                                    }
                                                >
                                                    <AppIcon
                                                        name={"icl-x"}
                                                        w="1rem"
                                                        color={
                                                            "var(--chakra-colors-primary)"
                                                        }
                                                    />
                                                </Button>
                                            )}
                                        </HStack>
                                    </>
                                )}
                            </HStack>
                        </HStack>
                    </AppFormControl>
                </VStack>
            </form>
        </AppFormContainer>
    );
};
